import { FormEvent, KeyboardEvent, useState } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import CompanyImage from "../../images/png/LoginPageImage.png";

import type { AppDispatch, RootState } from "../../stores";
import {
  loginUser,
  getAuthenticatedUser,
} from "../../features/auth-module/authAction";
import { logout } from "../../features/auth-module/authSlice";

interface ContentProps {
  component?: React.ElementType;
  noValidate?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5),
  background: "#fff",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
    minHeight: "55vh",
    order: 2,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    justifyContent: "flex-start",
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundSize: "cover",
  backgroundPosition: "center",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  position: "relative",
  borderRadius: 10,
  transform: "scale(1)",
  [theme.breakpoints.down("md")]: {
    minHeight: "35vh",
    order: 1,
    backgroundPosition: "top center",
    backgroundSize: "120%",
  },
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const Content = styled(Box)<ContentProps>(({ theme }) => ({
  width: "100%",
  maxWidth: 450,
  padding: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  marginTop: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8),
    maxWidth: "100%",
    marginTop: theme.spacing(1.5),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "28px",
  fontWeight: 700,
  color: "#455354",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#6E6E6E",
  fontSize: "12px",
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    marginBottom: theme.spacing(1.5),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const SignInButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  width: "100%",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: "6px",
  padding: theme.spacing(1),
  fontSize: "12px",
  "&:hover": {
    backgroundColor: "#354142",
  },
  "&:disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    minHeight: "37px",
  },
}));

const Login = () => {
  const [formData, setFormData] = useState({
    emailId: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    emailId: "",
    password: "",
  });
  const [touched, setTouched] = useState({
    emailId: false,
    password: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { loading } = useSelector((state: RootState) => state.auth);

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "emailId":
        if (!value) return "Email Address is required";
        if (value.includes(" ")) return "Email Address cannot contain spaces";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim()))
          return "Enter a valid email address (e.g., user@domain.com)";
        return "";
      case "password":
        if (!value) return "Password is required";
        if (value.includes(" ")) return "Password cannot contain spaces";
        if (value.length < 8) return "Password must be at least 8 characters";
        return "";
      default:
        return "";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (value.includes(" ")) {
      toast.error(
        `${
          name === "emailId" ? "Email Address" : "Password"
        } cannot contain spaces`
      );
      return;
    }

    const trimmedValue = value.trim();
    setFormData((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));

    if (touched[name as keyof typeof touched]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, trimmedValue),
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(
        name,
        formData[name as keyof typeof formData].trim()
      ),
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const newErrors = {
      emailId: validateField("emailId", formData.emailId),
      password: validateField("password", formData.password),
    };

    setErrors(newErrors);
    setTouched({
      emailId: true,
      password: true,
    });

    if (newErrors.emailId || newErrors.password) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const loginResult = await dispatch(
        loginUser({
          email: formData.emailId,
          password: formData.password,
        })
      ).unwrap();

      if (loginResult.success) {
        const profileResult = await dispatch(getAuthenticatedUser()).unwrap();

        if (profileResult.success) {
          const profileUser = profileResult.data?.user;
          if (profileUser?.role?.rolename === "admin") {
            navigate("/main/admin-dashboard");
          } else if (profileUser?.role?.rolename === "customer") {
            if (profileUser?.subscription?.id) {
              navigate("/main/dashboard");
            } else {
              navigate("/subscription-plan");
            }
          }
        }
      }
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const handleRegisterClick = (e: React.MouseEvent) => {
    e.preventDefault();
    dispatch(logout());
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.clear();
    sessionStorage.clear();
    navigate("/register");
  };

  const backgroundImageUrl = CompanyImage;

  return (
    <Container>
      <ImageSection sx={{ backgroundImage: `url(${backgroundImageUrl})` }} />
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Content
            component="form"
            onSubmit={(e: FormEvent) => handleSubmit(e)}
            onKeyDown={(e: KeyboardEvent) => handleKeyPress(e)}
          >
            <WelcomeText>Welcome, Log In</WelcomeText>
            <Subtitle>Login to your account to continue...</Subtitle>

            <FieldLabel>Email Address</FieldLabel>
            <StyledTextField
              autoFocus
              name="emailId"
              variant="outlined"
              type="text"
              autoComplete="email"
              value={formData.emailId}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.emailId}
              helperText={errors.emailId}
              required
              size={isMobile ? "small" : "medium"}
            />

            <Box
              sx={{
                width: "100%",
                mt: 0.5,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Typography
                variant="body2"
                color="primary"
                component={RouterLink}
                to="/forgot-password"
                sx={{
                  fontFamily: "'Almarai', sans-serif",
                  color: "#77BC9C",
                  textDecoration: "none",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                }}
              >
                Forgot Password?
              </Typography>
            </Box>

            <FieldLabel>Password</FieldLabel>
            <StyledTextField
              name="password"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="At least 8 characters"
              value={formData.password}
              onChange={handleChange}
              onBlur={handleBlur}
              error={!!errors.password}
              helperText={errors.password}
              required
              size={isMobile ? "small" : "medium"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size={isMobile ? "small" : "medium"}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            <motion.div
              whileHover={{
                scale: 1.01,
                boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
              }}
              whileTap={{ scale: 0.97 }}
              style={{ width: "100%", marginTop: "12px" }}
            >
              <SignInButton
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : null
                }
              >
                {loading ? "Logging in..." : "Log in"}
              </SignInButton>
            </motion.div>

            <Box
              sx={{
                width: "100%",
                mt: 1.5,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  fontFamily: "'Almarai', sans-serif",
                  color: "#455354",
                }}
              >
                Don't have an account?
              </Typography>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/register"
                color="primary"
                sx={{
                  textDecoration: "none",
                  ml: 0.5,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  fontFamily: "'Almarai', sans-serif",
                  color: "#455354",
                }}
                onClick={handleRegisterClick}
              >
                Create one
              </Typography>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default Login;
